import type { TFunction } from 'i18next';
import type { DriverRef } from './types';

/**
 * The driver attribution shown on a trip row, a trip-replay header, an alert row
 * and a behaviour event (PLAN §"Trips, alerts and behaviour events show the
 * driver").
 *
 * 🔴 THREE STATES, AND THE UNKNOWN ONE IS SAID IN WORDS.
 *   - `by assignment` — a permanent/temporary assignment covered the instant.
 *   - `set by owner`   — an owner picked the driver for this trip (manual fix).
 *   - `unknown driver` — `driver === null`. A real state, never a blank: a fleet
 *     manager must be able to tell "nobody was assigned" from "we forgot to show
 *     it". Same discipline as Behaviour's honest empty state.
 *
 * Counts only, never a score (Mahad, 3 Sep) — there is nothing numeric here.
 */

function PersonGlyph() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
      className="flex-none"
    >
      <path d="M20 21a8 8 0 1 0-16 0 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
    </svg>
  );
}

export function DriverChip({
  driver,
  t,
  className,
}: {
  driver: DriverRef;
  t: TFunction;
  className?: string;
}) {
  if (driver === null) {
    return (
      <span
        data-testid="driver-chip"
        data-driver="unknown"
        className={['inline-flex items-center gap-1 text-[11.5px] text-raad-dim', className ?? ''].join(' ')}
      >
        <PersonGlyph />
        <span>{t('drivers.unknown')}</span>
      </span>
    );
  }
  return (
    <span
      data-testid="driver-chip"
      data-driver={driver.id}
      className={['inline-flex items-center gap-1 text-[11.5px] text-raad-muted', className ?? ''].join(' ')}
    >
      <PersonGlyph />
      <span className="font-semibold text-raad-text">{driver.name}</span>
      <span className="text-raad-dim">· {t(`drivers.source.${driver.source}`)}</span>
    </span>
  );
}
